import React from 'react'
import styled from 'styled-components'
import { BlockLink, Button, Card, Flex, Text, Box, Stamp } from 'pcln-design-system'
import { Ac, Flights, FreeCancellation, Guests, Luggage, Mileage } from 'pcln-icons'
import { CarListing, HotelListing } from './types'
import * as PCLNAnalytics from '../../../utils/PCLN-Analytics'

const ProductWrapper = styled(Card)`
  height: 185px;
  width: 273px;
  background-color: white;
  margin-right: 12px;
  ${props => props.theme.mediaQueries[0]} {
    margin-right: 15px;
  }
`
const HotelImage = styled(Box)`
  min-height: 100px;
  width: 100px;
  height: 102px;
  background-image: url(${props => props.bgImage});
`
const CarImage = styled(Box)`
  height: ${props => props.height};
  width: ${props => props.width};
  background-image: url(${props => props.bgImage});
  background-repeat: no-repeat;
  background-size: contain;

`
const ProductTitle = styled(Text)`
  font-size: 14px;
  font-weight: bold;
  line-height: 19px;
  margin-bottom: 6px;
  hyphens: auto;
  display: -webkit-box;
  -moz-box-orient: vertical;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
`
const PrimaryText = styled(Text)`
  font-size: 14px;
`
const SecondaryText = styled(Text)`
  font-size: 12px;
`
const SmallText = styled(Text)`
  font-size: 10px;
`
const ProductFlexBorder = styled(Flex)`
  border-bottom: 1px solid rgba(89,107,125,.5);
`
const FeatureBox = styled(Box)`
  border-radius: 2px;
  margin-right: 8px;
`
const Location = styled(Text)`
  font-size: 12px;
  text-overflow: ellipsis;
  font-weight: bold;
  color: gray;
  white-space: nowrap;
  overflow: hidden;
  width: 135px;
`

function trackCrossSellClicks() {
  PCLNAnalytics.send({
    action: 'Feature Box CTA',
    data: 'Cross Sell Choose Button'
  })
}

export const CrossSellHotel = (props: HotelListing) => {
  const {
    currencySymbol,
    currentPrice,
    guestRating,
    href,
    imageUrl,
    location,
    name,
    originalPrice,
    savingPercentage,
    starRating,
  } = props

  return (
    <ProductWrapper boxShadowSize='md' width={1} >
      <ProductFlexBorder>
        <HotelImage bgImage={imageUrl}/>
        <Flex flexDirection='column' width='172px' px={3} py={2}>
          <ProductTitle>{name}</ProductTitle>
          <Location mb={2}>{location}</Location>
          <Flex alignItems='center'>
            <FeatureBox color='alert'>
              <SecondaryText color='text.lightest' px='6px' fontWeight='bold'>{guestRating}</SecondaryText>
            </FeatureBox>
            <SecondaryText fontWeight='bold'>{starRating}-Star Hotel</SecondaryText>
          </Flex>
        </Flex>
      </ProductFlexBorder>
      <Flex py={2} flexDirection='column' mb={1}>
        <Flex alignItems='center' mb='6px' px={3}>
          <Stamp bg='primary.dark' style={{ border: 'none', borderRadius: '2px', minHeight: '20px'}}>
            <Flights size='12' color='text.lightest' />
            <Text fontSize='10px' px={1} color='text.lightest'>Priceline VIP</Text>
          </Stamp>
          {savingPercentage > 0 &&
            <PrimaryText color='primary.dark' px='6px' fontWeight='bold'>{savingPercentage}% off</PrimaryText>
          }
        </Flex>
        <Flex alignItems='center' justifyContent='space-between'>
          <Flex flexDirection='column' px={3}>
            {originalPrice > 0 &&
              <SecondaryText fontWeight='bold'>was {currencySymbol}{originalPrice}</SecondaryText>
            }
            <Flex alignItems='baseline' mt={originalPrice === 0 ? 3 : 0}>
              <Text fontSize={3} fontWeight='bold' mr={1} color='primary.dark'><sup>{currencySymbol}</sup>{currentPrice}</Text>
              <PrimaryText color='text.light' fontSize={0}>per night</PrimaryText>
            </Flex>
          </Flex>
          <BlockLink onClick={trackCrossSellClicks}
            href={href} target='_new'>
            <Button color='secondary' mx={1} href={href}>Choose</Button>
          </BlockLink>
        </Flex>
      </Flex>
    </ProductWrapper>
  )
}

export const CrossSellCar = (props: CarListing) => {
  const {
    carExample,
    carImageUrl,
    href,
    checkoutUrl,
    partner: {
        url
    },
    pricing: {
        priceDescription
    },
    rate: {
        currencySymbol,
        dailyPrice,
        isFreeCancellation,
        savingsPriceTotal,
        totalPrice
    },
    vehicleFeatures: {
        bagCapacity,
        isAirConditioned,
        isUnlimitedMileage,
        peopleCapacity
    }
  } = props

  const vehicleFeature = (Icon: any, text: string | Number, isBold: boolean) => {
    return (
      <Flex alignItems='center'>
        <Icon size={14} mr={1}/>
        <SmallText fontWeight={isBold ? 'bold' : undefined}>{text}</SmallText>
      </Flex>
    )
  }

  return (
    <ProductWrapper boxShadowSize='md'>
      <ProductFlexBorder>
        <Flex flexDirection='column' ml={2} my='5px'>
          <CarImage height='58px' width='100px' bgImage={carImageUrl}/>
          <Flex justifyContent='space-between'>
            {vehicleFeature(Guests, peopleCapacity, false)}
            {vehicleFeature(Luggage, bagCapacity, false)}
            {isAirConditioned ? vehicleFeature(Ac, 'AC', false) : null}
          </Flex>
        </Flex>
        <Flex flexDirection='column' width='172px' px={2} py={2}>
          <ProductTitle style={{marginBottom: '0px'}}>Economy Car</ProductTitle>
          <SmallText color='text.light' mb={1}>{carExample}</SmallText>
          {isFreeCancellation ? vehicleFeature(FreeCancellation, 'Free Cancellation', true) : null}
          {isUnlimitedMileage ? vehicleFeature(Mileage, 'Unlimited Mileage', true) : null}
        </Flex>
      </ProductFlexBorder>
      <Flex flexDirection='column' px={3} py={2}>
        <Flex justifyContent='space-between' alignItems='flex-start'>
          <Flex flexDirection='column'>
            <Stamp fontSize='10px' bg='alert' style={{ border: 'none', borderRadius: '2px', minHeight: '20px', width: 'fit-content'}}>
                <Text color='text.lightest'>
                  PAY NOW &amp; SAVE
                </Text>
            </Stamp>
              <SmallText style={{ width: 'fit-content'}} mt={1}>Save <strong>{currencySymbol}{savingsPriceTotal}</strong> by paying now</SmallText>
          </Flex>
          <Flex alignItems='baseline' color='secondary'>
            <Text fontSize={4} fontWeight='light'>+</Text>
            <Text fontSize={4} fontWeight='bold' mr={1}> <sup>{currencySymbol}</sup>{dailyPrice}</Text>
            <PrimaryText>{priceDescription}</PrimaryText>
          </Flex>
        </Flex>
        <Flex justifyContent='space-between' alignItems='flex-end' mt='-3px'>
          <CarImage height='35px' width='100px' bgImage={url}/>
          <Flex flexDirection='column' float='right'>
            <Flex flexDirection='row-reverse'><SecondaryText color='text.light'>Total: {currencySymbol}{totalPrice}</SecondaryText></Flex>
              <BlockLink onClick={trackCrossSellClicks} href={checkoutUrl} target='_new'>
                <Button style={{fontSize: '12px', height: '35px'}} color='secondary'>Choose</Button>
              </BlockLink>
          </Flex>
        </Flex>
      </Flex>
    </ProductWrapper>
  )
}
